<template>
  <ReportCard :title="$t('report.clients_by_card')" header-margin="mb-4">
    <template #header>
      <button @click.stop="showCardBreakdownInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
        :title="$t('report.clients_card_breakdown_info.title')">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>

    <template v-if="byCardEntries.length > 0">
      <!-- Transactions distribution bar -->
      <div class="flex items-center gap-2 mb-1">
        <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground w-12 shrink-0">
          {{ $t('report.transactions') }}
        </span>
        <div class="flex w-full h-[6px] rounded-full overflow-hidden">
          <div v-for="(entry, idx) in byCardEntries" :key="'dprop-' + entry.name"
            class="h-full transition-all duration-500 first:rounded-l-full last:rounded-r-full"
            :style="{
              width: cardDocumentsProportion(entry.stats) + '%',
              backgroundColor: cardColor(entry.name, idx)
            }">
          </div>
        </div>
      </div>

      <!-- CA distribution bar -->
      <div class="flex items-center gap-2 mb-1.5">
        <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground w-12 shrink-0">
          {{ $t('report.clients_mix_revenue') }}
        </span>
        <div class="flex w-full h-[6px] rounded-full overflow-hidden">
          <div v-for="(entry, idx) in byCardEntries" :key="'rprop-' + entry.name"
            class="h-full transition-all duration-500 first:rounded-l-full last:rounded-r-full"
            :style="{
              width: cardRevenueProportion(entry.stats) + '%',
              backgroundColor: cardColor(entry.name, idx)
            }">
          </div>
        </div>
      </div>

      <!-- Unified legend showing % CA -->
      <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 mb-4">
        <span v-for="(entry, idx) in byCardEntries" :key="'leg-' + entry.name"
          class="flex items-center gap-1">
          <span class="w-1.5 h-1.5 rounded-full shrink-0"
            :style="{ backgroundColor: cardColor(entry.name, idx) }"></span>
          <span class="text-[10px] font-semibold text-muted-foreground">
            {{ cardDisplayName(entry.name) }}
            <span class="font-bold" :style="{ color: cardColor(entry.name, idx) }">
              {{ cardRevenueProportion(entry.stats).toFixed(0) }}%
            </span>
          </span>
        </span>
      </div>

      <!-- Card type rows -->
      <div class="flex flex-col gap-2">
        <div v-for="(entry, idx) in byCardEntries" :key="'row-' + entry.name"
          class="ct-card-row relative rounded-lg overflow-hidden">
          <div class="absolute left-0 inset-y-0 w-[3px]"
            :style="{ backgroundColor: cardColor(entry.name, idx) }"></div>

          <div class="pl-3.5 pr-3 py-2.5">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[12px] font-bold text-foreground leading-none">{{ cardDisplayName(entry.name) }}</span>
              <span class="text-[12px] font-bold text-foreground tabular-nums">
                {{ entry.name === '_not_member' ? entry.stats.total_documents : entry.stats.total_clients }}
                <span class="text-[9px] font-semibold text-muted-foreground/60 ml-0.5">
                  {{ entry.name === '_not_member' ? $t('report.documents_label').toLowerCase() : $t('report.clients_label').toLowerCase() }}
                </span>
              </span>
            </div>

            <div class="flex items-center gap-1.5 flex-wrap">
              <span class="ct-metric-chip ct-metric-chip--revenue">
                {{ formatCentimes(entry.stats.total_revenue.included_tax) }}
              </span>
              <span v-if="entry.stats.avg_purchases_per_client != null" class="ct-metric-chip">
                x{{ entry.stats.avg_purchases_per_client.toFixed(1) }}
              </span>
              <span v-if="entry.stats.avg_basket != null" class="ct-metric-chip">
                {{ formatCentimes(entry.stats.avg_basket) }}/ticket
              </span>
              <span v-if="entry.stats.avg_revenue_per_client != null" class="ct-metric-chip">
                {{ formatCentimes(entry.stats.avg_revenue_per_client) }}/client
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <p class="text-[13px] font-semibold text-muted-foreground text-center py-4">
        {{ $t('report.clients_no_card_data') }}
      </p>
    </template>
  </ReportCard>

  <BottomSheet v-model:is-open="showCardBreakdownInfo" variant="sidebar">
    <ClientsCardBreakdownInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { SalesByClientsResponse, ClientsCardStats } from '../../responses'
import { useAppSettingsStore } from '../../stores/AppSettingsStore'
import { formatCurrencyForCenter } from '../../utils/Languages'
import ReportCard from '../ReportCard.vue'
import BottomSheet from '../BottomSheet.vue'
import ClientsCardBreakdownInfoSheet from '../ClientsCardBreakdownInfoSheet.vue'

const props = defineProps<{
  response: SalesByClientsResponse | null
  centerId: number
}>()

const { t } = useI18n()
const appSettingsStore = useAppSettingsStore()
const centerObj = computed(() => appSettingsStore.getCenter(props.centerId))
const showCardBreakdownInfo = ref(false)

// --- Card helpers ---
const CARD_COLORS = ['#06C8FF', '#F59E0B', '#8B5CF6', '#10B981', '#F43F5E']
const NOT_MEMBER_COLOR = '#C4CAD4'

function cardColor(name: string, idx: number): string {
  if (name === '_not_member') return NOT_MEMBER_COLOR
  return CARD_COLORS[idx % CARD_COLORS.length]
}

function cardDisplayName(name: string): string {
  if (name === '_not_member') return t('report.clients_not_member')
  if (name.toLowerCase() === 'sans carte') return t('report.clients_no_card')
  return name
}

function formatCentimes(val: number | null | undefined): string {
  if (val == null) return '-'
  return formatCurrencyForCenter(val, centerObj.value)
}

function getByCard<T>(byCard: Record<string, T> | never[]): Record<string, T> {
  if (Array.isArray(byCard)) return {}
  return byCard
}

const byCardSafe = computed(() => {
  if (!props.response) return {}
  return getByCard<ClientsCardStats>(props.response.by_card)
})

const byCardEntries = computed(() => {
  return Object.entries(byCardSafe.value)
    .map(([name, stats]) => ({ name, stats }))
    .sort((a, b) => {
      if (a.name === '_not_member') return 1
      if (b.name === '_not_member') return -1
      return b.stats.total_revenue.included_tax - a.stats.total_revenue.included_tax
    })
})

const totalByCardDocuments = computed(() => {
  return byCardEntries.value.reduce((sum, e) => sum + e.stats.total_documents, 0)
})

function cardDocumentsProportion(stats: ClientsCardStats): number {
  if (totalByCardDocuments.value === 0) return 0
  return (stats.total_documents / totalByCardDocuments.value) * 100
}

const totalByCardRevenue = computed(() => {
  return byCardEntries.value.reduce((sum, e) => sum + e.stats.total_revenue.included_tax, 0)
})

function cardRevenueProportion(stats: ClientsCardStats): number {
  if (totalByCardRevenue.value === 0) return 0
  return (stats.total_revenue.included_tax / totalByCardRevenue.value) * 100
}
</script>

<style scoped>
.ct-card-row {
  background: rgba(0, 0, 0, 0.02);
}
:is(.dark *).ct-card-row {
  background: rgba(255, 255, 255, 0.03);
}

.ct-metric-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  color: var(--foreground);
  opacity: 0.7;
  background: rgba(0, 0, 0, 0.03);
}
:is(.dark *).ct-metric-chip {
  background: rgba(255, 255, 255, 0.05);
}
.ct-metric-chip--revenue {
  opacity: 1;
  font-weight: 700;
  color: #009FE3;
  background: rgba(6, 200, 255, 0.08);
}
:is(.dark *).ct-metric-chip--revenue {
  color: #06C8FF;
  background: rgba(6, 200, 255, 0.12);
}
</style>
